/**
 * Guard for the window.pmw global
 *
 * Every file of this library attaches itself to window.pmw, and the files
 * written as ES modules (the logger, the consent module, the listener files)
 * reach the library through that global name on every call. The whole library
 * therefore depends on nobody assigning a new object to window.pmw once it has
 * started. Somebody does: WordPress printed the library's own configuration
 * as `var pmw = {...}` right before the script tag, and a consent tool in
 * auto-blocking mode (Cookiebot) or a JavaScript optimizer can hold that line
 * back and run it after this script, or run it a second time. From that
 * moment on window.pmw was a bare configuration object. The files written as
 * IIFEs kept working against the object they had captured, everything that
 * went through the global name threw "pmw.<method> is not a function", and
 * the shop lost part of its tracking without a word (support #6960: Cookiebot
 * held jQuery and the configuration line, the shop had marked the library
 * script as data-cookieconsent="ignore", so the library ran first and lost its
 * object when Cookiebot re-ran the configuration about a second later).
 *
 * This file loads before anything else and keeps the global pointing at the
 * one object the library is built on:
 *
 * - When the library created window.pmw itself, or the shop's page assigned
 *   it, the property is configurable and becomes an accessor. Reading it
 *   returns the library, assigning to it merges the assigned object's data into
 *   the library. Nothing is lost, not even for a call in the same tick.
 * - When window.pmw was declared with `var` (the WordPress case), the property
 *   cannot be redefined. main() then re-checks the global after every await,
 *   and a watchdog checks it during the page's lifetime; a foreign object is
 *   merged into the library and the library is put back.
 *
 * Data from the foreign object wins over plain data on the library, which is
 * how a re-run configuration line keeps delivering its values, but it never
 * replaces one of the library's functions.
 *
 * Every takeover is reported three ways, because a shop needs to be able to
 * measure this and not only read about it in a console it never opens: the
 * console warning, a `pmw:warning:global-replaced` bus event carrying the
 * foreign object's keys and the running count, and `pmw.globalGuard`, whose
 * restoredCount() can be read at any later time.
 *
 * @since 1.67.1
 */

(function () {

	const lib = window.pmw = window.pmw || {};

	let restored = 0;

	/**
	 * Copy the foreign object's data onto the library.
	 *
	 * @param {*} foreign
	 * @returns {void}
	 */
	const merge = foreign => {

		if (!foreign || typeof foreign !== "object" || foreign === lib) return;

		Object.keys(foreign).forEach(key => {

			// A shop that wrote `var pmw = {...}` carries data only. Should a
			// key collide with one of the library's functions, the function
			// stays: losing it is the very failure this file exists to prevent.
			if (typeof lib[key] === "function") return;

			lib[key] = foreign[key];
		});
	};

	/**
	 * Report a foreign assignment: a console warning for whoever opens the
	 * console, and a bus event for whoever wants to count it.
	 *
	 * The warning alone turned out to be unmeasurable in the field: the shop
	 * that reported this (support #6960) runs Microsoft Clarity, which
	 * records errors and not warnings, so they could confirm that the errors
	 * had stopped but not how often the race still happened. The event and
	 * the counter below close that gap: a snippet can forward the event to an
	 * analytics tool, and pmw.globalGuard.restoredCount() answers the same
	 * question at any later point, including for assignments that landed
	 * before the snippet registered.
	 *
	 * @param {*} foreign
	 * @returns {void}
	 */
	const report = foreign => {

		const keys = foreign && typeof foreign === "object" ? Object.keys(foreign) : [];

		// eslint-disable-next-line no-console
		console.warn(`Pixel Manager: another script assigned a new object to window.pmw while the tracking library was running (its keys: ${keys.join(", ") || "none"}). The library kept its own object and merged that data into it. This is usually the plugin's own configuration line, run a second time by a consent tool in auto-blocking mode or by a JavaScript optimizer. See https://sweetcode.com/docs/pmw/caching-and-optimization`);

		// The bus loads immediately after this file, and an assignment can
		// land before it does, so nothing here may assume it exists. Payload
		// events stay on emit() by the bus' own rule, so this is not paired
		// with a DOM dispatch.
		try {
			if (lib.bus && typeof lib.bus.emit === "function") {
				lib.bus.emit("pmw:warning:global-replaced", {keys, count: restored});
			}
		} catch (e) {
			// Reporting must never be the thing that breaks the page.
		}
	};

	/**
	 * Put the library back if something else took over the global.
	 *
	 * @returns {boolean} true when the global had been replaced
	 */
	const reclaim = () => {

		const current = window.pmw;

		if (current === lib) return false;

		merge(current);

		try {
			window.pmw = lib;
		} catch (e) {
			// A read-only global cannot be restored. The merge above at least
			// carried the data over; the callers keep failing loudly.
		}

		restored++;
		report(current);

		return true;
	};

	let accessor = false;

	const descriptor = Object.getOwnPropertyDescriptor(window, "pmw");

	if (descriptor && descriptor.configurable) {

		try {
			Object.defineProperty(window, "pmw", {
				configurable: true,
				enumerable  : true,
				get() {
					return lib;
				},
				set(value) {
					if (value === lib) return;
					merge(value);
					restored++;
					report(value);
				},
			});

			accessor = true;
		} catch (e) {
			accessor = false;
		}
	}

	if (!accessor) {

		// A consent tool re-runs held scripts once its configuration has
		// arrived, seconds after the page loaded; an optimizer that delays
		// scripts runs them on the first interaction, which can be much later.
		// Check often while the page starts, then keep a cheap eye on it.
		const startedAt = Date.now();
		let interval    = 50;
		let timer       = null;

		const tick = () => {

			reclaim();

			if (interval === 50 && Date.now() - startedAt > 20000) {
				clearInterval(timer);
				interval = 500;
				timer    = setInterval(tick, interval);
			}
		};

		timer = setInterval(tick, interval);
	}

	// The same two answers on the library itself, so a shop can read them from
	// a snippet: how often the global was taken over, and whether the property
	// could be defended with an accessor or is being watched by the watchdog.
	lib.globalGuard = {
		isAccessor   : () => accessor,
		restoredCount: () => restored,
	};

	module.exports = {
		lib,
		reclaim,
		isAccessor   : () => accessor,
		restoredCount: () => restored,
	};

}());
